import { NewCapitalGlobalLayout } from '@/features/quotes/components/capital-global/new-capital-global-layout.component';
import { NewQuoteLayout } from '@/features/quotes/components/layout/new-quote-layout.component';
import { NewRdEquipamentosLayout } from '@/features/quotes/components/rd-equipamentos/new-rd-equipamentos-layout.component';
import { portalStatusToRdStep } from '@/features/quotes/utils/rd-equipamentos/portal-status-to-rd-step.util';
import { rdEquipamentosService } from '@/services/quotes/rd-equipamentos';
import type { ProposalRisk } from '@/services/proposals/types';
import type { CatalogId } from '@/shared/utils/catalog-label.util';
import { getFormattedCurrency } from '@/shared/utils/currencyFormatter';
import type { ComponentType } from 'react';

export type ProductCode = '140001' | '180007' | '930015' | '930016' | '0171';

/**
 * Código do RD Equipamentos — chave do registry abaixo e mesmo valor do back
 * (`RD_UNIFIED_PRODUCT_CODE`). Só para os componentes do próprio RD; as telas
 * compartilhadas não comparam `productCode`, leem o registry.
 */
export const RD_PRODUCT_CODE: ProductCode = '0171';

export type QuoteWizardFlow = 'new' | 'edit' | 'view';

type LayoutProps = {
  productId: string;
  category: string;
  productCode: string;
  stepCode: string;
  quoteFlow: QuoteWizardFlow;
};

export type ProductBusinessRules = {
  /** Fallback max when Morte coverage is absent from the API catalog (Step 2). */
  maxIndividualCapital?: number;
  /**
   * Teto de capital APENAS INFORMATIVO por cobertura (código → valor em R$).
   * Exibido no caption da linha ("Max: R$ …") sem nunca bloquear o Step 2.
   *
   * Existe porque no InsureMO o `SumInsuredMax` acumula duas funções num campo
   * só — exibir o teto e travar a contratação. Quando o teto é apenas
   * referência de aceitação, o campo é REMOVIDO do produto (para não travar) e
   * o valor a exibir passa a morar aqui. O `sumInsuredMax` vindo da API, quando
   * presente, continua sendo trava de verdade e tem precedência sobre este mapa
   * (ex.: Diárias R$ 500, Rescisão Trabalhista R$ 15.000).
   */
  informationalSumInsuredMax?: Readonly<Record<string, number>>;
  /**
   * Coberturas cujo `SumInsuredMax` a API AINDA envia, mas que é só referência
   * de aceitação — o corretor pode passar do valor.
   *
   * Complementa o `informationalSumInsuredMax`: lá o campo já foi removido do
   * Tech Product e o valor a exibir mora no registry; aqui o campo continua
   * vindo da API (porque o produto ainda não foi alterado) e o front rebaixa o
   * teto para apenas-exibição. O número na tela continua sendo o da API.
   */
  displayOnlySumInsuredMax?: readonly string[];
};

/* eslint-disable no-unused-vars */
/**
 * Um campo da seção "Dados do Risco" do card de locais (resumo da proposta,
 * análise de subscrição e inspeção).
 *
 * O que varia por produto é **esta declaração**, não o componente: o card
 * recebe os campos já resolvidos e só renderiza. Produto novo com outro
 * conjunto de campos entra adicionando `riskFields` na entrada dele — nenhum
 * `.tsx` é tocado e nenhum `if (productCode === ...)` é criado.
 */
export type RiskFieldDescriptor = {
  label: string;
  /** Colunas ocupadas no grid de 3 do card. */
  span?: 1 | 2 | 3;
  /** Catálogo que traduz o código em descrição. Sem catálogo, exibe `value`. */
  catalog?: CatalogId;
  /**
   * Código(s) do risco a traduzir. Uma lista vira um texto único sem
   * repetições (ex.: "Objeto Segurado" tem um item por objeto no `scheduleExt`).
   */
  code?: (
    _risk: ProposalRisk
  ) =>
    | string
    | number
    | ReadonlyArray<string | number | null | undefined>
    | null
    | undefined;
  /**
   * Texto pronto. Com `catalog`, é a descrição já resolvida pelo BFF (`*Desc`)
   * e serve de fallback quando o código não está no catálogo; sem `catalog`, é
   * o próprio valor exibido.
   */
  value?: (_risk: ProposalRisk) => string | undefined;
};
/* eslint-enable no-unused-vars */

/**
 * Empresarial (180007). Fonte de cada campo confirmada nas duas pontas do
 * fluxo (payload da cotação e mapper de volta) e coerente com o card da
 * apólice e com o PDF gerado pelo BFF.
 *
 * Atenção ao "Objeto Segurado": ele vem do `scheduleExt`, **não** de
 * `propertyClassification` — esse último é gravado com o default fixo `'1'`
 * pelo wizard e não representa escolha do corretor.
 */
const BUSINESS_RISK_FIELDS: readonly RiskFieldDescriptor[] = [
  {
    label: 'Tipo de Atividade Realizada',
    catalog: 'business-activities',
    code: risk => risk.activityTypePerformed,
    value: risk => risk.activityTypePerformedDesc,
  },
  {
    label: 'Tipo de Construção',
    span: 2,
    catalog: 'construction-types',
    code: risk => risk.constructionType,
    value: risk => risk.constructionTypeDesc,
  },
  {
    label: 'Imóvel Tombado?',
    // O Empresarial grava 'Y' e o Residencial 'S' — aceita os dois, como o
    // card da apólice já faz (`policy-tab.component.tsx`).
    value: risk => {
      const flag = String(risk.listedPropertyFlag ?? '').toUpperCase();
      return flag === 'Y' || flag === 'S' ? 'Sim' : 'Não';
    },
  },
  {
    label: 'Objeto Segurado',
    catalog: 'insured-objects',
    code: risk => (risk.scheduleExt ?? []).map(item => item.insuredObject),
  },
  {
    label: 'Valor em Risco',
    value: risk => {
      const items = risk.scheduleExt ?? [];
      if (items.length === 0) return undefined;
      const total = items.reduce(
        (sum, item) => sum + (Number(item.value) || 0),
        0
      );
      return getFormattedCurrency(total);
    },
  },
];

export interface ProductConfig {
  productCode: ProductCode;
  displayName: string;
  schemaCategory: string;
  layoutComponent: ComponentType<LayoutProps>;
  proposalService:
    | 'homeowner'
    | 'business'
    | 'capital-global'
    | 'life-group'
    | 'rd-equipamentos';
  /** Proposta com wizard/rota dedicados (ex.: Capital Global). */
  usesCapitalGlobalProposal?: boolean;
  /** Produto com fluxo CQRS próprio (Group Life PME) — edição redireciona
   *  para o wizard dedicado em vez do EditQuoteForm comum. */
  usesGroupLifeProposal?: boolean;
  businessRules?: ProductBusinessRules;
  /**
   * Campos de "Dados do Risco" do card de locais. Ver `RiskFieldDescriptor`.
   * Sem declaração, o produto herda o conjunto do Empresarial (ver
   * `getProductRiskFields`).
   */
  riskFields?: readonly RiskFieldDescriptor[];
  /**
   * Produto com backend próprio, fora do InsureMO (ex.: RD Equipamentos). Nas
   * listas compartilhadas (Cotações, Propostas, Apólices, Análises) as linhas
   * dele abrem no wizard do produto em vez das telas InsureMO, e não clonam
   * (a clonagem é do InsureMO). Ver `getOwnBackend`/`getProductWizardRoute`.
   */
  ownBackend?: OwnBackendConfig;
  /**
   * Onde o produto define as formas de pagamento. Ausente = no Portal. Ver
   * `PaymentMethodsSource`.
   */
  paymentMethodsSource?: PaymentMethodsSource;
}

/**
 * - `portal`: catálogo de formas e cobrança pelo gateway, editados em
 *   Configurações › Produtos › Formas de pagamento.
 * - `calculation-engine`: as formas vêm do i4pro, sincronizadas pelo Motor de
 *   Cálculo, e o corretor escolhe na própria cotação. A tela de formas de
 *   pagamento só informa isso: nada editado lá vale para o produto.
 */
export type PaymentMethodsSource = 'portal' | 'calculation-engine';

/* eslint-disable no-unused-vars */
export type OwnBackendConfig = {
  /** Passo do wizard a retomar, a partir do `StatusPortal` da linha. */
  resumeStep: (_portalStatus: number | string) => string;
  /** Tela de detalhe da apólice; recebe o id da linha em `$proposalId`. */
  policyDetailsRoute: string;
  /** PDF da apólice, baixado do backend do produto. */
  fetchPolicyPdf: (_id: string) => Promise<{ blob: Blob; filename: string }>;
};
/* eslint-enable no-unused-vars */

export const CAPITAL_GLOBAL_PRODUCT_CODE: ProductCode = '930015';
export const GROUP_LIFE_PRODUCT_CODE: ProductCode = '930016';

// NewQuoteLayout tipa `category` como BrokerProductCategory; o search param
// é string. Cast aqui mantém o tipo do registry consistente sem mexer no
// componente compartilhado.
const productConfigs: Record<ProductCode, ProductConfig> = {
  '140001': {
    productCode: '140001',
    displayName: 'Residencial',
    schemaCategory: 'HOME_INSURANCE',
    layoutComponent: NewQuoteLayout as ComponentType<LayoutProps>,
    proposalService: 'homeowner',
  },
  '180007': {
    productCode: '180007',
    displayName: 'Empresarial',
    schemaCategory: 'BUSINESS_INSURANCE',
    layoutComponent: NewQuoteLayout as ComponentType<LayoutProps>,
    proposalService: 'business',
    riskFields: BUSINESS_RISK_FIELDS,
  },
  '930015': {
    productCode: '930015',
    displayName: 'Capital Global',
    schemaCategory: 'OTHER',
    layoutComponent: NewCapitalGlobalLayout,
    proposalService: 'capital-global',
    usesCapitalGlobalProposal: true,
    // Used only as fallback when Morte is missing from the coverage catalog.
    businessRules: {
      maxIndividualCapital: 500_000,
    },
  },
  '930016': {
    productCode: '930016',
    displayName: 'Seguro PME - Vida em Grupo',
    schemaCategory: 'GROUP_LIFE_INSURANCE',
    layoutComponent: NewQuoteLayout as ComponentType<LayoutProps>,
    usesGroupLifeProposal: true,
    proposalService: 'life-group',
    businessRules: {
      // R$ 500.000 é referência de aceitação, não limite de contratação: o
      // `SumInsuredMax` destas 7 coberturas foi REMOVIDO do Tech Product 930016
      // para não travar a cotação. Acima do valor a cotação segue e a regra
      // 930016_UW_03_CapitalSegurado a encaminha para análise técnica (U1). O
      // número fica aqui só para o corretor continuar vendo o teto.
      informationalSumInsuredMax: {
        '9300001': 500_000, // Morte
        '8200002': 500_000, // Morte Acidental
        '9300012': 500_000, // Invalidez Funcional Permanente e Total por Doença
        '9300013': 500_000, // Morte - Cônjuge
        '8200012': 500_000, // Morte Acidental - Cônjuge
        '8200011': 500_000, // Invalidez Perm. Total ou Parcial por Acidente - Cônjuge
        '9300009': 500_000, // Invalidez Funcional Perm. e Total por Doença - Cônjuge
      },
      // Invalidez Permanente Total ou Parcial por Acidente (titular e cônjuge):
      // o SumInsuredMax de R$ 1.000.000 ainda vem da API, mas é referência de
      // aceitação e não limite de contratação — o corretor pode passar dele. O
      // valor segue aparecendo na linha, agora só como informação.
      displayOnlySumInsuredMax: [
        '8200003', // Invalidez Perm. Total ou Parcial por Acidente
        '8200011', // Invalidez Perm. Total ou Parcial por Acidente - Cônjuge
      ],
    },
  },
  '0171': {
    productCode: '0171',
    displayName: 'RD Equipamentos',
    schemaCategory: 'RD_EQUIPMENT',
    layoutComponent: NewRdEquipamentosLayout as ComponentType<LayoutProps>,
    proposalService: 'rd-equipamentos',
    ownBackend: {
      resumeStep: portalStatusToRdStep,
      policyDetailsRoute: '/quotes/rd-equipamentos/$proposalId/apolice',
      fetchPolicyPdf: id => rdEquipamentosService.policyPdf(id),
    },
    paymentMethodsSource: 'calculation-engine',
  },
};

export function getProductConfig(
  productCode: string
): ProductConfig | undefined {
  return productConfigs[productCode as ProductCode];
}

/** Config de backend próprio do produto; `undefined` para produto InsureMO. */
export function getOwnBackend(
  productCode: string | undefined
): OwnBackendConfig | undefined {
  if (!productCode) return undefined;
  return getProductConfig(productCode)?.ownBackend;
}

/**
 * Rota do wizard do produto para uma linha das listas compartilhadas, já no
 * passo que o status da linha pede. `undefined` para produto InsureMO — a tela
 * segue a rota InsureMO de sempre.
 */
export function getProductWizardRoute(
  productCode: string | undefined,
  id: string,
  portalStatus: number | string,
  quoteFlow: QuoteWizardFlow = 'new'
) {
  const config = productCode ? getProductConfig(productCode) : undefined;
  if (!config?.ownBackend) return undefined;
  return {
    to: '/quotes/products/new/$id' as const,
    params: { id },
    search: {
      productId: config.productCode,
      category: config.schemaCategory,
      productCode: config.productCode,
      stepCode: config.ownBackend.resumeStep(portalStatus),
      quoteFlow,
    },
  };
}

/**
 * Produtos de backend próprio: as listas compartilhadas só os trazem quando
 * filtradas pelo código deles (isolamento do BFF — ver
 * `UnifiedListProviderRegistry`).
 */
export function listOwnBackendProductCodes(): ProductCode[] {
  return listProductConfigs()
    .filter(config => config.ownBackend)
    .map(config => config.productCode);
}

/**
 * Todos os produtos do registry, para telas que precisam iterar o catálogo
 * (ex.: a configuração de modo de emissão em Configurações > Produtos).
 *
 * Produto novo aparece nessas telas só por entrar no registry — nenhum
 * componente precisa ser tocado.
 */
/** Onde o produto define as formas de pagamento; desconhecido ou sem declaração = no Portal. */
export function getPaymentMethodsSource(
  productCode: string
): PaymentMethodsSource {
  return getProductConfig(productCode)?.paymentMethodsSource ?? 'portal';
}

export function listProductConfigs(): ProductConfig[] {
  return (Object.keys(productConfigs) as ProductCode[]).map(
    code => productConfigs[code]
  );
}

/**
 * Ramo (`lobId`) do produto: os dois primeiros dígitos do código (`930015` → `93`).
 * Mesma derivação que o BFF usa para reservar o nº de apólice no balde.
 */
export function getProductLobId(productCode: string): number {
  return Number(productCode.slice(0, 2));
}

/**
 * Rótulo de um ramo, derivado do registry de produtos — não existe enum paralelo de
 * ramos para manter, então produto novo passa a aparecer sozinho.
 *
 * Um ramo pode ter mais de um produto (93 = Capital Global + Vida PME); nesse caso os
 * nomes são listados. Ramo sem produto cadastrado devolve `undefined` e a UI exibe o
 * número cru.
 */
export function getLobLabel(lobId: number): string | undefined {
  const names = (Object.keys(productConfigs) as ProductCode[])
    .filter(code => getProductLobId(code) === lobId)
    .map(code => productConfigs[code].displayName);

  return names.length ? names.join(', ') : undefined;
}

/**
 * Tetos de capital apenas informativos do produto (código da cobertura → R$).
 * Ver `ProductBusinessRules.informationalSumInsuredMax`.
 */
export function getInformationalSumInsuredMax(
  productCode: string | undefined
): Readonly<Record<string, number>> {
  if (!productCode) return {};
  return (
    getProductConfig(productCode)?.businessRules?.informationalSumInsuredMax ??
    {}
  );
}

export function getDisplayOnlySumInsuredMax(
  productCode: string | undefined
): readonly string[] {
  if (!productCode) return [];
  return (
    getProductConfig(productCode)?.businessRules?.displayOnlySumInsuredMax ?? []
  );
}

/**
 * Campos de "Dados do Risco" do card de locais, por produto.
 *
 * O fallback é o conjunto do Empresarial porque a tela de análise hoje trata
 * toda proposta como empresarial (a rota só desvia o Vida PME) — manter o
 * fallback preserva o que os demais produtos já exibem. Quando cada produto
 * ganhar os campos dele, basta declarar `riskFields` na entrada do registry.
 */
export function getProductRiskFields(
  productCode: string | undefined
): readonly RiskFieldDescriptor[] {
  if (!productCode) return BUSINESS_RISK_FIELDS;
  return getProductConfig(productCode)?.riskFields ?? BUSINESS_RISK_FIELDS;
}

export function getMaxIndividualCapital(
  productCode: string = CAPITAL_GLOBAL_PRODUCT_CODE
): number | undefined {
  return getProductConfig(productCode)?.businessRules?.maxIndividualCapital;
}

// Resolve o layout do produto com fallback para o layout legado, mantendo
// o comportamento atual quando o productCode é desconhecido.
export function resolveProductLayout(
  productCode: string
): ComponentType<LayoutProps> {
  return (
    getProductConfig(productCode)?.layoutComponent ??
    (NewQuoteLayout as ComponentType<LayoutProps>)
  );
}
